import { CabysItem, HaciendaApi } from "@/atv/HaciendaApi";
import { isValidCabysCode, normalizeCabysCode } from "@utils/Cabys";

/**
 * Lo que el catalogo de Hacienda sabe de cada CAByS consultado.
 *
 * - `CabysItem`: el codigo existe y trae su tarifa de IVA en `impuesto`.
 * - `null`: Hacienda respondio que el codigo no existe.
 * - **ausente del Map**: no se pudo consultar (red, 307, timeout). Quien lo lea debe tratar
 *   la ausencia como "no se sabe" y no como error del dato, para no bloquear una emision
 *   porque `api.hacienda.go.cr` esta caido.
 */
export type CabysLookup = Map<string, CabysItem | null>;

/** Cache de proceso: el catalogo de Hacienda no cambia dentro de una sesion. */
const catalogCache: CabysLookup = new Map();

/** Los CAByS distintos y validos de un conjunto de lineas de documento. */
export const distinctCabysCodes = (rawCodes: (string | null | undefined)[]): string[] => {
    const codes = new Set<string>();

    rawCodes.forEach((raw) => {
        const code = normalizeCabysCode(raw);
        if (isValidCabysCode(code)) {
            codes.add(code);
        }
    });

    return [...codes];
};

export type ResolveCabysOptions = {
    /**
     * Tope de espera total. Al vencer se devuelve lo que se alcanzo a resolver, para que un
     * camino sensible a la latencia (emitir una factura) no quede colgado de Hacienda.
     */
    timeoutMs?: number;
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Consulta el catalogo de Hacienda para cada codigo y devuelve lo que se pudo resolver.
 *
 * Nunca lanza: un fallo de red deja el codigo fuera del Map. Secuencial a proposito, son
 * pocos codigos distintos por documento y `HaciendaApi` ya reintenta los 307 del balanceador.
 */
export const resolveCabysCodes = async (
    codes: string[],
    options: ResolveCabysOptions = {}
): Promise<CabysLookup> => {
    // Se muta en el lugar para que, si vence el tope de espera, sobreviva lo ya resuelto.
    const result: CabysLookup = new Map();

    const resolveAll = async () => {
        for (const code of distinctCabysCodes(codes)) {
            if (catalogCache.has(code)) {
                result.set(code, catalogCache.get(code) ?? null);
                continue;
            }

            try {
                const items = await HaciendaApi.getCabysByCodigo(code);
                const item = items?.[0] ?? null;
                catalogCache.set(code, item);
                result.set(code, item);
            } catch (error) {
                // Sin respuesta de Hacienda no se cachea nada: el proximo intento vuelve a probar.
                console.warn(`No fue posible consultar el CAByS ${code} en Hacienda`, error);
            }
        }
    };

    if (options.timeoutMs) {
        await Promise.race([resolveAll(), delay(options.timeoutMs)]);
    } else {
        await resolveAll();
    }

    return result;
};

/** Solo para los tests: limpia la cache de proceso. */
export const clearCabysCatalogCache = (): void => {
    catalogCache.clear();
};
